import React from "react";
import _ from "lodash";
import Select from "react-select";
import TagItem from "components/common/TagItem";
import CustomSpecificKeyValueImportModal from "./CustomSpecificKeyValueImportModal";
import RemovableLabel from "./RemovableLabel";
import { inputClass } from "helpers/StyleClass";
import { HARDCODE } from "constants/CustomTarget";

import { NetworkAPI, UnitAPI } from "apis";

import {
  k16ToReadable,
  k20ToReadable,
  readableToString,
} from "components/ops-mgmt/account/In2wKeyConvertHelper";

import { FaPlus } from "react-icons/fa";

let cache = {};

const ORDERMAP = new Map().set("in2w_upa_price", getNameAsNumber());

function getNameAsNumber() {
  return [
    function (item) {
      if (!item.name) return 0;
      return parseInt(item.name);
    },
  ];
}

class CustomTargetingValuesSelector extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpenImportModal: false,
      isImportKey: false,

      isLoading: false,
      items: [],
      options: [],
      notFound: [],
      selectedValue: null,
      inputValue: "",

      errMsg: null,
    };

    this.asyncFn = null;

    this.handleChange = this.handleChange.bind(this);
    this.handleSelectInputChange = this.handleSelectInputChange.bind(this);
    this.handleTextInputChange = this.handleTextInputChange.bind(this);
    this.handleImport = this.handleImport.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleResetInput = this.handleResetInput.bind(this);
    this.handleRemoveNotFound = this.handleRemoveNotFound.bind(this);
    this.customFilter = this.customFilter.bind(this);
    this.parseInputToOptions = this.parseInputToOptions.bind(this);
    this.parseSelectedToString = this.parseSelectedToString.bind(this);
  }

  async componentDidMount() {
    const { networkId, keyId } = this.props;
    if (!networkId) {
      return;
    }

    if (!keyId) {
      return;
    }

    this.asyncFn = await this.queryCustomKeyValues();
  }

  async queryCustomKeyValues() {
    const { networkId, keyId, managedValueIds, benchmarkValueIds, keyName } =
      this.props;
    this.setState({ isLoading: true });
    const isHardcode = keyName === HARDCODE.KEY23;

    const isImportKey = _.includes(
      [HARDCODE.KEY16, HARDCODE.KEY20, HARDCODE.UPA_PRICE],
      keyName
    );

    this.setState({
      isImportKey,
    });

    try {
      let items = [];
      let units = [];
      const cacheKey = `${networkId}_${keyId}`;

      if (isHardcode) {
        const cacheKeyUnit = `${networkId}_${keyName}`;
        if (cache[cacheKeyUnit]) {
          units = cache[cacheKeyUnit];
        } else {
          const result = await UnitAPI.getUnitFullpathHashList({
            networkId,
          });
          units = result;
          cache[cacheKeyUnit] = units;
        }
      }

      if (cache[cacheKey]) {
        items = cache[cacheKey];
      } else {
        const r = await NetworkAPI.getCustomTargetingValues({
          networkId,
          ids: [keyId],
        });
        items = r.items;
        cache[cacheKey] = items;
      }

      let options = _.map(items, (r) => {
        // name is displayed different based on matchType
        // PREFIX: {value}*
        // BROAD: ~{value}
        // BROAD_PREFIX: ~{value}*
        // EXACT: {value}
        const mt = r.matchType;
        const front = mt === "BROAD" || mt === "BROAD_PREFIX" ? "~" : "";
        const back = mt === "PREFIX" || mt === "BROAD_PREFIX" ? "*" : "";
        let name = `${front}${r.name}${back}`;
        const unit = _.find(units, { fullpathHash: r.name });

        const showDisplayName = r.displayName && r.displayName !== name;

        let readableName = null;

        if (keyName === HARDCODE.KEY16) {
          readableName = k16ToReadable(name);
        } else if (keyName === HARDCODE.KEY20) {
          readableName = k20ToReadable(name);
        }

        return {
          ...r,
          value: r.id,
          name,
          label: `${r.id} - ${name}${
            showDisplayName ? ` (${r.displayName})` : ""
          }${
            isHardcode && unit
              ? ` [${unit.unitId} (${unit.extUnitId}) ${unit.name}]`
              : ""
          }${readableName ? ` [${readableToString(readableName)}]` : ""}`,
          unitId: unit ? unit.unitId : null,
          extUnitId: unit ? unit.extUnitId : null,
          readableName,
        };
      });
      options = _.sortBy(options, ORDERMAP.get(keyName) || "name");

      let selectedValue = null;
      let notFound = null;
      if (managedValueIds && managedValueIds.length > 0) {
        const mids = _.map(managedValueIds, _.parseInt);
        selectedValue = _.filter(options, (option) => {
          return _.indexOf(mids, option.id) !== -1;
        });
        notFound = _.difference(mids, _.map(selectedValue, "id"));
      } else if (benchmarkValueIds && benchmarkValueIds.length > 0) {
        selectedValue = _.filter(options, (option) => {
          return _.indexOf(benchmarkValueIds, option.id) !== -1;
        });
        notFound = _.difference(benchmarkValueIds, _.map(selectedValue, "id"));
      }

      let parsedNotFound = [];
      if (notFound && notFound.length > 0) {
        parsedNotFound = _.map(notFound, (id) => {
          const item = _.find(items, { id });
          return item ? `${item.id}-${item.name}` : `${id}`;
        });
      }

      this.setState(
        {
          items,
          options,
          notFound: parsedNotFound,
          errMsg: null,
          isLoading: false,
          inputValue: this.parseSelectedToString(selectedValue),
        },
        () => {
          if (selectedValue) {
            this.handleChange(selectedValue);
          }
        }
      );
    } catch (err) {
      console.log(err);
      this.setState({
        errMsg: `Error query custom targeting keys: ${err.toString()}`,
      });
    }

    this.setState({ isLoading: false });
  }

  async componentDidUpdate(prevProps) {
    if (_.parseInt(prevProps.keyId) !== _.parseInt(this.props.keyId)) {
      this.handleChange(null);
      this.setState({
        errMsg: null,
        inputValue: "",
      });
      await this.queryCustomKeyValues();
    }

    if (prevProps.valueKey !== this.props.valueKey) {
      // Reset inputValue when valueKey changes
      this.handleResetInput();
    }
  }

  componentWillUnmount() {
    if (this.asyncFn) {
      this.asyncFn.cancel();
    }
  }

  handleChange(option) {
    this.setState({
      selectedValue: option,
    });
    this.props.handleOnChange &&
      this.props.handleOnChange({
        options: option,
        notFound: this.state.notFound.map((d) => _.toInteger(d.split("-")[0])),
      });
  }

  handleSelectInputChange(input) {
    if (_.isEmpty(input)) return;
    // Check if there is seperator
    // user can paste a list of unit ids
    if (input.includes(",")) {
      const key = this.parseValueKey(this.props.valueKey || "id");
      const { selected, invalid } = this.parseInputToOptions(input, key);

      this.setState({
        errMsg:
          invalid.length > 0
            ? `Failed to find these ${key}s: ${invalid.join(", ")}`
            : null,
      });

      if (selected.length > 0) {
        const newValues = _.uniqBy(
          [...(this.state.selectedValue || []), ...selected],
          "id"
        );
        this.handleChange(newValues);
        return "";
      }
    }

    return input;
  }

  handleTextInputChange(input) {
    this.setState({ inputValue: input });

    const key = this.parseValueKey(this.props.valueKey || "id");
    const { selected, invalid, duplicate } = this.parseInputToOptions(
      input,
      key,
      true
    );
    this.setState({
      errMsg:
        invalid.length > 0 || duplicate.length > 0
          ? `${
              invalid.length > 0
                ? `Failed to find these ${key}s: ${invalid.join(", ")}.`
                : ""
            }${
              duplicate.length > 0
                ? `${
                    invalid.length > 0 ? " " : ""
                  }Duplicated ${key}s: ${duplicate.join(", ")}`
                : ""
            }`
          : null,
    });

    this.handleChange(selected);
  }

  handleImport(list) {
    const { options } = this.state;
    const { valueKey } = this.props;
    const optionsList = _.map(list, (id) =>
      _.find(options, { id: _.parseInt(id) })
    ).filter((opt) => opt);

    this.setState({
      errMsg: null,
      inputValue: optionsList
        .map((opt) => {
          return opt[valueKey || "name"];
        })
        .join(","),
    });
    this.handleChange(optionsList);
  }

  handleDelete(id) {
    const { selectedValue, inputValue } = this.state;
    const { valueKey } = this.props;
    if (!selectedValue) return;

    const selected = _.find(selectedValue, { id });

    if (selected) {
      const selectedInput = selected[valueKey || "id"];
      if (inputValue.includes(`${selectedInput},`)) {
        this.setState({
          inputValue: inputValue.replace(`${selectedInput},`, ""),
        });
      } else if (inputValue.includes(`,${selectedInput}`)) {
        this.setState({
          inputValue: inputValue.replace(`,${selectedInput}`, ""),
        });
      } else if (inputValue.includes(selectedInput)) {
        this.setState({
          inputValue: inputValue.replace(selectedInput, ""),
        });
      }
    }

    const newSelectedValue = _.filter(selectedValue, (item) => {
      return item.id !== id;
    });

    this.handleChange(newSelectedValue);
  }

  handleResetInput() {
    const { selectedValue } = this.state;
    this.setState({
      errMsg: null,
      inputValue: this.parseSelectedToString(selectedValue),
    });
  }

  handleRemoveNotFound() {
    this.setState(
      {
        notFound: [],
      },
      () => {
        if (this.props.handleRemoveNotFound) {
          this.props.handleRemoveNotFound({
            options: this.state.selectedValue,
          });
        }
      }
    );
  }

  parseValueKey(key) {
    if (key === "gam_unit_id") return "unitId";
    if (key === "ext_gam_unit_id") return "extUnitId";
    return key;
  }

  customFilter(option, searchText) {
    if (typeof searchText !== "string") {
      return false;
    }

    const { valueKey } = this.props;
    // label includes: id, name, displayName
    const searchKey = this.parseValueKey(valueKey || "label");

    if (
      option.data[searchKey]
        .toString()
        .toLowerCase()
        .includes(searchText.toLowerCase())
    ) {
      return true;
    } else {
      return false;
    }
  }

  parseInputToOptions(input, key, inputOrder = false) {
    const { options } = this.state;
    let parts = input.split(",");
    parts = _.map(_.compact(parts), _.trim);
    if (key === "name") parts = _.map(parts, _.toLower);

    // Filter selected: match the input with options
    const selectedValue = inputOrder
      ? _.map(parts, (part) => {
          return _.find(options, (option) => {
            return key === "name"
              ? _.toLower(option[key]) === part
              : option[key].toString() === part;
          });
        }).filter((d) => d)
      : _.filter(options, (option) => {
          return (
            _.indexOf(
              parts,
              key === "name" ? _.toLower(option[key]) : option[key].toString()
            ) !== -1
          );
        });

    // Filter duplicate: duplicate values in selectedValue
    const uniqList = _.uniqBy(selectedValue, "id");
    const duplicateList = _.keys(_.countBy(selectedValue, key)).filter(
      (k) => _.countBy(selectedValue, key)[k] > 1
    );

    // Filter invalid: values in parts but not in selectedValue
    const invalidIds = _.difference(
      parts,
      _.map(selectedValue, (d) =>
        key === "name" ? _.toLower(d[key]) : d[key].toString()
      )
    );

    return {
      selected: uniqList,
      invalid: invalidIds,
      duplicate: duplicateList,
    };
  }

  parseSelectedToString(selected) {
    const { valueKey, keyName } = this.props;
    return selected &&
      _.includes([HARDCODE.KEY16, HARDCODE.KEY20, HARDCODE.UPA_PRICE], keyName)
      ? selected
          .map((d) => {
            return d[valueKey || "name"];
          })
          .join(",")
      : "";
  }

  render() {
    const { keyName, isReadonly, isMulti, valueKey, showActionButtons } =
      this.props;
    const {
      options,
      selectedValue,
      notFound,
      errMsg,
      isLoading,
      isOpenImportModal,
      isImportKey,
      inputValue,
    } = this.state;

    return (
      <div className="relative">
        {!isImportKey ? (
          <Select
            value={selectedValue}
            onChange={this.handleChange}
            options={options}
            isMulti={isMulti}
            isClearable
            isLoading={isLoading}
            onInputChange={this.handleSelectInputChange}
            isDisabled={isReadonly}
            filterOption={this.customFilter}
          />
        ) : (
          <>
            {!isReadonly && (
              <input
                type="text"
                value={inputValue}
                className={inputClass + " mb-1 w-full"}
                placeholder={`Enter ${keyName} value ${valueKey}, separated by comma`}
                onChange={(e) => this.handleTextInputChange(e.target.value)}
                disabled={isLoading}
              />
            )}
            <div className="flex items-center gap-2 ">
              <div
                className={`border rounded overflow-y-auto ${
                  isReadonly ? "border-gray-300" : "border-gray-400"
                }`}
                style={{
                  maxHeight: "8rem",
                  padding: "0.35rem 0.5rem",
                  backgroundColor: isReadonly ? "#F2F2F2" : "#FFFFFF",
                }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {isLoading && (
                    <div
                      className="text-sm text-gray-600"
                      style={{ padding: "0.1rem" }}
                    >
                      Loading ...
                    </div>
                  )}

                  {selectedValue &&
                    selectedValue.map((item, idx) => {
                      return (
                        <TagItem
                          key={item.id}
                          readOnly={isReadonly}
                          onDelete={() => this.handleDelete(item.id)}
                        >
                          {item.label}
                        </TagItem>
                      );
                    })}
                </div>
              </div>
              {!isLoading && !isReadonly && (
                <div
                  className="border shadow rounded flex cursor-pointer items-center gap-1 border-dashed border-gray-400 p-1 text-sm font-semibold text-blue-600 hover:bg-gray-100 hover:text-blue-700"
                  onClick={() => {
                    this.setState({ isOpenImportModal: true });
                  }}
                >
                  <FaPlus />
                  Add
                </div>
              )}
            </div>
          </>
        )}

        {errMsg &&
          (isImportKey ? (
            <RemovableLabel
              bgColor="bg-red-100"
              textColor="text-red-800"
              text={errMsg}
              hint="Remove"
              onClick={this.handleResetInput}
              iconOnly={false}
            ></RemovableLabel>
          ) : (
            <RemovableLabel
              bgColor="bg-red-100"
              textColor="text-red-800"
              text={errMsg}
              onClick={this.handleResetInput}
            ></RemovableLabel>
          ))}

        {notFound.length > 0 && (
          <RemovableLabel
            bgColor="bg-orange-100"
            textColor="text-orange-600"
            text={`Not found value ids: ${notFound.join(", ")}`}
            hint="Remove"
            onClick={!isReadonly ? this.handleRemoveNotFound : undefined}
            iconOnly={false}
          ></RemovableLabel>
        )}

        {isOpenImportModal && (
          <CustomSpecificKeyValueImportModal
            keyName={keyName}
            data={options}
            selected={selectedValue}
            handleImport={(selected) => {
              this.setState({ isOpenImportModal: false });
              this.handleImport(selected);
            }}
            handleClose={() => this.setState({ isOpenImportModal: false })}
          />
        )}
      </div>
    );
  }
}

export default CustomTargetingValuesSelector;
